import { ReactNode } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import UIcon from "lib/icons/untitled_ui/UIcon";

export function LeadSection({
  title,
  actions,
  children,
}: {
  title: string;
  /** Rendered right-aligned on the title row (e.g. a "find a contact" action). */
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Flex column gap={12} aria-label={`${title} section`}>
      <TitleRow>
        <Text h3>{title}</Text>
        {actions}
      </TitleRow>
      <Flex column gap={12}>
        {children}
      </Flex>
    </Flex>
  );
}

/** A group within a section, labelled only when a section shows more than one (e.g. Saved / Recommended). */
export function LeadSectionGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Flex column gap={12}>
      <GroupLabel>{label}</GroupLabel>
      {children}
    </Flex>
  );
}

/**
 * Placeholder row for work the backend is still doing (researching the buyer, searching for
 * contacts). Occupies a card-sized row so the section doesn't sit empty while a lead's
 * post-creation workflows run — the `spinner` icon animates itself.
 */
export function LeadLoadingRow({ children }: { children: ReactNode }) {
  return (
    <Row role="status">
      <UIcon icon="spinner" size={16} color="sysTextSecondary" />
      <Text body color="sysTextPlaceholder">
        {children}
      </Text>
    </Row>
  );
}

export function LeadEmptyRow({ children }: { children: ReactNode }) {
  return (
    <Row>
      <Text body color="sysTextPlaceholder">
        {children}
      </Text>
    </Row>
  );
}

const TitleRow = styled(Flex)({
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  minHeight: 32,
});

const Row = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  gap: 16,
  minHeight: 56,
  padding: 16,
  boxSizing: "border-box",
  borderRadius: 8,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  background: theme.colors.sysBackgroundDefault,
}));

const GroupLabel = styled.span(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.5,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: theme.colors.sysTextPlaceholder,
}));
